import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { friendlyError } from "@/lib/errors";
import { AuthLegalLinks } from "@/components/layout/AuthLegalLinks";
import {
  clearPersistedInvitationToken,
  invitationLoginPath,
  persistInvitationToken,
  readPersistedInvitationToken,
} from "@/lib/access-invitation";
import {
  consumeAuthCallbackCredential,
  parseAuthCallbackSearch,
} from "@/lib/auth-callback";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [status, setStatus] = useState("Confirming your email…");
  const [inviteHref, setInviteHref] = useState("/register");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = parseAuthCallbackSearch(window.location.search, window.location.hash);
      const invite = persistInvitationToken(params.inviteToken) ?? readPersistedInvitationToken();
      if (invite) {
        persistInvitationToken(invite);
        setInviteHref(`/register?invite=${encodeURIComponent(invite)}`);
      }

      const result = await consumeAuthCallbackCredential(
        {
          verifyOtp: (opts) => supabase.auth.verifyOtp(opts),
          exchangeCodeForSession: (code) => supabase.auth.exchangeCodeForSession(code),
          getSession: () => supabase.auth.getSession(),
        },
        params,
      );
      if (cancelled) return;
      if (result.ok === false) {
        setError(friendlyError(result.error));
        setStatus("Confirmation did not complete");
        return;
      }

      try {
        window.history.replaceState({}, document.title, `${window.location.pathname}${invite ? `?invite=${encodeURIComponent(invite)}` : ""}`);
      } catch {
        /* ignore */
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;
      if (!session?.user) {
        navigate(invite ? invitationLoginPath(invite) : "/login", { replace: true });
        return;
      }

      const meta = (session.user.user_metadata ?? {}) as { full_name?: string; org_name?: string };
      const { error: initError } = await supabase.rpc("initialize_account", {
        p_email: session.user.email ?? "",
        p_full_name: meta.full_name ?? null,
        p_org_name: meta.org_name ?? null,
        p_invite_token: invite,
      });
      if (cancelled) return;
      if (initError) {
        setError(friendlyError(initError.message));
        setStatus("Email confirmed. Sign in to finish joining.");
        return;
      }
      clearPersistedInvitationToken();
      await refresh();
      navigate("/dashboard", { replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, refresh, searchParams]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 text-center">
        <div className="flex items-center justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center mr-3">
            <i className="ri-stack-line text-white" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">Tradivo</h1>
        </div>
        <h2 className="text-lg font-semibold text-slate-800 mb-2">{status}</h2>
        {error ? (
          <>
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <p className="text-sm text-slate-500 mb-4">
              Request a new confirmation email from your invitation link if this one has expired.
            </p>
            <Link to={inviteHref} className="text-sm font-medium text-emerald-600 hover:text-emerald-700">
              Back to invitation
            </Link>
          </>
        ) : (
          <p className="text-sm text-slate-500">Please wait a moment.</p>
        )}
        <AuthLegalLinks />
      </div>
    </div>
  );
}
